import { type ChildId, type Device, type PairingCode, parsePairingUri } from '@kidshub/contracts';
import { type FormEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../i18n/use-locale.js';
import { apiClient } from '../lib/api-client.js';
import { toErrorKey } from '../lib/error-code.js';
import { toInitial } from '../lib/turkish-case.js';
import { useResource } from '../lib/use-resource.js';
import { AppShell } from '../ui/app-shell.js';
import { Button, ButtonLink, Card, EmptyState, Icon } from '../ui/kit.js';
import { QrScanner } from './qr-scanner.js';
import './devices.css';

/**
 * Adding a tablet.
 *
 * No account is created on the child's device. The tablet shows a code, the
 * parent claims it from this authenticated session, and the tablet receives its
 * own scoped credential — the parent's token is never copied to it
 * (`SPEC.md §19`, invariant §6.5). The screen says so, because a parent
 * handing a child a tablet is entitled to know what is on it.
 *
 * One question follows the scan, and only one: whose tablet is this? The device
 * opens straight into that child's library.
 */

type Stage =
  | { name: 'SCANNING' }
  | { name: 'CHOOSING_CHILD'; pairingCode: PairingCode }
  | { name: 'PAIRED'; device: Device };

export const PairDevicePage = () => {
  const { t } = useTranslation();
  const locale = useLocale();
  const { resource } = useResource('children', () => apiClient.children.list());
  const codeId = useId();

  const [stage, setStage] = useState<Stage>({ name: 'SCANNING' });
  const [manualCode, setManualCode] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [claimingChildId, setClaimingChildId] = useState<ChildId | null>(null);
  // The camera opens on request rather than with the page: most parents at a
  // laptop will type the code, and a permission prompt they did not ask for
  // reads as the page wanting something from them.
  const [isCameraOpen, setIsCameraOpen] = useState(false);

  const accept = (scanned: string) => {
    const parsed = parsePairingUri(scanned);

    if (parsed.status === 'UNSUPPORTED_VERSION') {
      setErrorKey('pairing.unsupportedVersion');
      return;
    }
    if (parsed.status === 'INVALID') {
      setErrorKey('pairing.invalidCode');
      return;
    }

    setErrorKey(null);
    setStage({ name: 'CHOOSING_CHILD', pairingCode: parsed.pairingCode });
  };

  const submitManualCode = (event: FormEvent) => {
    event.preventDefault();
    accept(manualCode);
  };

  const claim = async (pairingCode: PairingCode, childId: ChildId) => {
    setErrorKey(null);
    setClaimingChildId(childId);

    try {
      const { device } = await apiClient.devices.claimPairing({ pairingCode, childId });
      setStage({ name: 'PAIRED', device });
    } catch (error) {
      setErrorKey(toErrorKey(error));
      // The code is single-use and short-lived, so a failed claim means
      // starting again with a fresh one rather than retrying this one.
      setStage({ name: 'SCANNING' });
    } finally {
      setClaimingChildId(null);
    }
  };

  const error =
    errorKey === null ? null : (
      <p className="help err pair-error" role="alert">
        {t(errorKey)}
      </p>
    );

  return (
    <AppShell title={t('pairing.title')} subtitle={t('pairing.subtitle')}>
      {stage.name === 'SCANNING' ? (
        <Card className="pair-card">
          {error}

          <div className="pair-grid">
            <div className="pair-opt">
              <h3>{t('pairing.scanHeading')}</h3>
              {isCameraOpen ? (
                <>
                  <QrScanner onResult={accept} />
                  <Button variant="ghost" onClick={() => setIsCameraOpen(false)}>
                    {t('pairing.closeCamera')}
                  </Button>
                </>
              ) : (
                <>
                  <p className="help">{t('pairing.scanBody')}</p>
                  <Button variant="primary" icon="camera" onClick={() => setIsCameraOpen(true)}>
                    {t('pairing.openCamera')}
                  </Button>
                </>
              )}
            </div>

            <div className="pair-or">{t('pairing.or')}</div>

            <form className="pair-opt" onSubmit={submitManualCode}>
              <div className="field">
                <label htmlFor={codeId}>{t('pairing.manualCodeLabel')}</label>
                <input
                  id={codeId}
                  className="input code-input"
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  value={manualCode}
                  onChange={(event) => setManualCode(event.target.value)}
                  placeholder={t('pairing.manualCodePlaceholder')}
                  aria-describedby={`${codeId}-help`}
                />
                <p className="help" id={`${codeId}-help`}>
                  {t('pairing.manualCodeHelp')}
                </p>
              </div>
              <Button type="submit" variant="secondary">
                {t('pairing.useManualCode')}
              </Button>
            </form>
          </div>

          <p className="t-meta pair-note">
            <Icon name="lock" small />
            {t('pairing.scopedTokenNote')}
          </p>
        </Card>
      ) : null}

      {stage.name === 'CHOOSING_CHILD' ? (
        <Card
          className="pair-card pair-narrow"
          title={t('pairing.whoseTablet')}
          meta={t('pairing.whoseTabletBody')}
        >
          {error}

          {resource.status === 'loading' ? <p className="t-meta">{t('common.loading')}</p> : null}

          {resource.status === 'ready' && resource.data.children.length === 0 ? (
            <EmptyState
              title={t('pairing.noChildren')}
              actions={
                <ButtonLink variant="primary" to="/children">
                  {t('nav.children')}
                </ButtonLink>
              }
            />
          ) : null}

          {resource.status === 'ready' && resource.data.children.length > 0 ? (
            <ul className="pick-list">
              {resource.data.children.map((entry) => (
                <li key={entry.child.id}>
                  <button
                    type="button"
                    className="pick"
                    disabled={claimingChildId !== null}
                    aria-busy={claimingChildId === entry.child.id}
                    onClick={() => void claim(stage.pairingCode, entry.child.id)}
                  >
                    <span className="avatar" aria-hidden="true">
                      {toInitial(entry.child.name, locale)}
                    </span>
                    <span className="pick-text">
                      <b>{entry.child.name}</b>
                      <span>{t(`ageBand.${entry.child.ageBand}`)}</span>
                    </span>
                    <Icon name="chev" />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="pair-back">
            <Button
              variant="ghost"
              disabled={claimingChildId !== null}
              onClick={() => setStage({ name: 'SCANNING' })}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </Card>
      ) : null}

      {stage.name === 'PAIRED' ? (
        <Card className="pair-card pair-narrow">
          <div className="pair-done">
            <span className="felt-sq sage">
              <Icon name="check" />
            </span>
            <div>
              <h2 className="t-h2">{t('pairing.connected')}</h2>
              <p className="t-meta">{stage.device.name}</p>
            </div>
          </div>
          <p className="pair-done-note">{t('pairing.connectedNote')}</p>
          <ButtonLink variant="primary" block to="/devices">
            {t('pairing.goToDevices')}
          </ButtonLink>
        </Card>
      ) : null}
    </AppShell>
  );
};
